import { useState } from "react";
import {
  FunnelSimple,
  Handshake,
  MagnifyingGlass,
  Plus,
  X,
} from "@phosphor-icons/react";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { PartnersTable } from "@/components";
import { useDebounce, usePartners, useSearch } from "@/hooks";
import { ContentLayout } from "@/layout";
import { MODAL_ROUTES, ROUTES } from "@/router";
import { useNavigateModal } from "@/router/useNavigateModal";
import { Breadcrumb, Button, Heading, MultiSelect, SectionHeader } from "@/ui";
import { tw } from "@/utils";

const inputClass =
  "block w-full rounded-xl border border-neutral-400 p-3 text-sm text-neutral-950 placeholder:text-neutral-400 hover:ring-1 hover:ring-tertiary-600 focus:shadow-input focus:outline-none focus:ring-1 focus:ring-tertiary-600";

const labelClass =
  "text-xs font-semibold uppercase tracking-wide text-neutral-500";

export const Partners = () => {
  const navigate = useNavigate();
  const navigateModal = useNavigateModal();
  const { searchQuery, handleSearchChange, setSearchQuery } = useSearch("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const { usePartnerCountries } = usePartners();
  const { data: countries } = usePartnerCountries();
  const countryOptions = (countries ?? []).map((country) => ({
    value: country,
    label: country,
  }));

  const { register, watch, reset } = useForm({
    defaultValues: {
      typeQuery: "",
      publishedQuery: "",
    },
  });

  const typeQuery = watch("typeQuery");
  const publishedQuery = watch("publishedQuery");

  const debouncedSearch = useDebounce(searchQuery);

  const activeFilterCount = [
    selectedCountries.length > 0,
    !!typeQuery,
    !!publishedQuery,
  ].filter(Boolean).length;

  const hasActiveFilters = !!searchQuery || activeFilterCount > 0;

  const clearAll = () => {
    setSearchQuery("");
    setSelectedCountries([]);
    reset({ typeQuery: "", publishedQuery: "" });
  };

  const breadcrumbItems = [
    {
      label: t("general.partners"),
      href: ROUTES.partners,
      icon: <Handshake />,
    },
  ];

  return (
    <ContentLayout className="flex flex-col gap-y-6">
      <Breadcrumb
        items={breadcrumbItems}
        current={t("general.partners")}
        onItemClick={(item) => {
          if (item.href) {
            navigate(item.href);
          }
        }}
      />
      <SectionHeader
        left={<Heading level={3} heading={t("general.partners")} />}
        right={
          <Button onClick={() => navigateModal(MODAL_ROUTES.addPartner)}>
            <Plus size={20} />
            {t("partner.add_partner")}
          </Button>
        }
      />

      {/* Search + Filters toggle */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex w-full flex-row items-center sm:max-w-md">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlass
                size={20}
                className={tw(
                  "text-neutral-950",
                  !searchQuery && "text-neutral-500",
                )}
              />
            </div>
            <input
              type="text"
              id="search"
              placeholder={t("partner.search_placeholder")}
              value={searchQuery}
              onChange={handleSearchChange}
              className={tw(inputClass, "pl-10")}
            />
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            className={tw(
              "flex items-center gap-2 rounded-xl border border-neutral-400 bg-white p-3 text-sm font-medium text-neutral-950 hover:bg-neutral-50",
              filtersOpen && "border-primary-600 bg-primary-50",
            )}
            aria-expanded={filtersOpen}
          >
            <FunnelSimple size={18} />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-700 px-1.5 text-xs font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAll}
              className="flex items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-900 sm:ml-auto"
            >
              <X size={16} />
              Clear all
            </button>
          )}
        </div>

        {/* Filters panel */}
        {filtersOpen && (
          <div className="grid grid-cols-1 gap-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Country</span>
              <MultiSelect
                placeholder={
                  selectedCountries.length
                    ? `${selectedCountries.length} selected`
                    : "Select countries"
                }
                options={countryOptions}
                selectedOptions={selectedCountries}
                onChange={setSelectedCountries}
                containerClassName="w-full"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Type</span>
              <select className={inputClass} {...register("typeQuery")}>
                <option value="">All types</option>
                <option value="community">Community</option>
                <option value="corporate">Corporate</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className={labelClass}>Published</span>
              <select className={inputClass} {...register("publishedQuery")}>
                <option value="">All</option>
                <option value="1">Published</option>
                <option value="0">Draft</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <PartnersTable
        searchQuery={debouncedSearch}
        filters={{
          country: selectedCountries,
          type: typeQuery,
          published: publishedQuery,
        }}
      />
    </ContentLayout>
  );
};
